import React, { useState } from "react";
import { useNotify, useRefresh } from "react-admin";
import DownloadIcon from "@mui/icons-material/Download";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import {
  CsvImportDialog,
  CsvImportTriggerButton,
  readFileAsText,
  useCanCsvImport,
  useCsvImportDialogState,
} from "@/components/csv-import";
import { getApiUrl } from "@/constants";
import httpClient from "@/dataprovider/http-client";
import { fmbDishRowsToImportPayload } from "./fmb-dish-import-payload";
import { downloadFmbDishCsvTemplate, parseFmbDishCsv } from "./parse-fmb-dish-csv";

type ImportResult = {
  created: number;
  updated: number;
  unchanged: number;
  skipped: number;
  errors: { rowNumber: number; message: string }[];
};

export function FmbDishCsvImportButton() {
  const notify = useNotify();
  const refresh = useRefresh();
  const canImport = useCanCsvImport(["fmbDish.create", "fmbDish.edit"]);
  const { open, busy, setBusy, inputRef, openDialog, closeDialog, chooseFile } =
    useCsvImportDialogState();
  const [lastResult, setLastResult] = useState<ImportResult | null>(null);

  if (!canImport) {
    return null;
  }

  const onFileSelected = async (file: File) => {
    setBusy(true);
    setLastResult(null);
    try {
      const text = await readFileAsText(file);
      let parsed;
      try {
        parsed = parseFmbDishCsv(text);
      } catch (err) {
        notify(err instanceof Error ? err.message : "Invalid CSV", { type: "error" });
        return;
      }

      const rowsPayload = fmbDishRowsToImportPayload(parsed.rows);

      if (rowsPayload.length === 0) {
        const parseErrs = parsed.parseErrors.map((pe) => ({
          rowNumber: pe.rowNumber,
          message: pe.message,
        }));
        setLastResult({
          created: 0,
          updated: 0,
          unchanged: 0,
          skipped: parsed.skippedBlankRows,
          errors: parseErrs,
        });
        notify(
          parseErrs.length > 0
            ? `No valid rows to import (${parseErrs.length} issue(s)).`
            : "No valid rows to import.",
          { type: "warning" }
        );
        return;
      }

      const headers = new Headers();
      headers.set("Content-Type", "application/json");

      const { json } = await httpClient(`${getApiUrl()}/fmbDish/bulk-import`, {
        method: "POST",
        headers,
        body: JSON.stringify({ rows: rowsPayload }),
      });

      const body = json as Partial<ImportResult> & {
        message?: string | string[];
        errors?: { rowNumber: number; message: string }[];
      };

      const serverErrors = Array.isArray(body.errors) ? body.errors : [];
      const parseErrs = parsed.parseErrors.map((pe) => ({
        rowNumber: pe.rowNumber,
        message: pe.message,
      }));
      const mergedErrors = [...parseErrs, ...serverErrors];

      const result: ImportResult = {
        created: body.created ?? 0,
        updated: body.updated ?? 0,
        unchanged: body.unchanged ?? 0,
        skipped: parsed.skippedBlankRows,
        errors: mergedErrors,
      };
      setLastResult(result);

      const touched = result.created + result.updated;
      if (touched > 0) {
        notify(`Imported ${result.created} new, updated ${result.updated} dish(es).`, {
          type: "success",
        });
      } else if (mergedErrors.length > 0) {
        notify(`No changes applied. ${mergedErrors.length} row issue(s).`, { type: "warning" });
      } else {
        notify("No changes applied (dishes already up to date).", { type: "info" });
      }
      refresh();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Import failed", { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        startIcon={<DownloadIcon />}
        onClick={downloadFmbDishCsvTemplate}
        size="small"
        variant="outlined"
      >
        Download template
      </Button>
      <CsvImportTriggerButton
        label="Import CSV"
        onClick={() => {
          setLastResult(null);
          openDialog();
        }}
      />

      <CsvImportDialog
        open={open}
        onClose={closeDialog}
        busy={busy}
        maxWidth="sm"
        title="Import dishes from CSV"
        progress={busy || undefined}
        fileInputRef={inputRef}
        onFileSelected={onFileSelected}
        actions={
          <>
            <Button onClick={closeDialog} disabled={busy}>
              Close
            </Button>
            <Button variant="outlined" disabled={busy} onClick={downloadFmbDishCsvTemplate}>
              Download template
            </Button>
            <Button variant="contained" disabled={busy} onClick={chooseFile}>
              Choose CSV file
            </Button>
          </>
        }
      >
        <Alert severity="info" sx={{ mb: 2 }}>
          Download the template, fill it in, then choose the file. Existing dishes with the same
          name are updated.
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontFamily: "monospace" }}>
          name,dietaryType,description,imageUrl
        </Typography>
        {lastResult ? (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Created {lastResult.created}, updated {lastResult.updated}, unchanged{" "}
              {lastResult.unchanged}, skipped blank rows {lastResult.skipped}.
            </Typography>
            {lastResult.errors.length > 0 ? (
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Row</TableCell>
                    <TableCell>Issue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lastResult.errors.slice(0, 50).map((err, idx) => (
                    <TableRow key={`${err.rowNumber}-${idx}-${err.message}`}>
                      <TableCell>{err.rowNumber}</TableCell>
                      <TableCell>{err.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : null}
            {lastResult.errors.length > 50 ? (
              <Typography variant="caption" color="text.secondary">
                Showing first 50 errors.
              </Typography>
            ) : null}
          </Box>
        ) : null}
      </CsvImportDialog>
    </>
  );
}
